import {
	useFetchChecklist,
	useFetchChecklistStepStatuses,
	useFetchCommentsByChecklistId,
} from "@/features/procedures/api/checklist.queries";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";

import { useFetchSuggestionsByChecklistId } from "@/service/ProcedureQueries";

export const useGetChecklistWithLiveData = (
	checklistId: number,
	procedureId: number,
) => {
	const { isLoading: isLoadingChecklist, data: checklist } = useFetchChecklist(
		checklistId,
		procedureId,
	);
	const { data: statuses } = useFetchChecklistStepStatuses(
		checklist?.ChecklistId ?? checklistId,
		procedureId,
	);
	const { data: comments } = useFetchCommentsByChecklistId(checklistId);
	const { data: suggestions } = useFetchSuggestionsByChecklistId(
		checklist?.ChecklistId ?? checklistId,
	);

	// Only block on checklist. Step statuses load in background (slow for
	// measurement checklists via SaveActiveStepsLiveTagValuesHandler).
	if (isLoadingChecklist) {
		return { data: undefined, isLoading: true };
	}

	// Create enriched steps array
	const enrichedSteps =
		checklist?.Steps?.map((step) => {
			const stepComments = comments?.[step?.Id] ?? [];

			const stepSuggestions = Array.isArray(suggestions)
				? suggestions.filter((s) => s.StepId === step.Id)
				: [];

			const status = Array.isArray(statuses)
				? statuses.find((x) => x.Id === step.Id)
				: undefined;

			const listenerTags = step.ListenerTags ?? [];
			const statusListenerTags = status?.ListenerTags ?? [];
			const enrichedListenerTags = listenerTags.map((tag) => {
				const tagValue = statusListenerTags.find(
					(tv) => tag.Tag === tv.LiveTag,
				);
				return {
					...tag,
					CurrentValue: tagValue?.CurrentValue,
					SavedTagValue: tagValue?.SavedTagValue,
				};
			});

			// keep fetched data immutable; build an enriched copy
			const enrichedStep: ProcedureNodeStep & {
				ManualMeasurementValue?: number;
				ManualMeasurementTimeStamp?: Date;
			} = {
				...step,
				Status: status?.Status ? 1 : 0,
				Comments: stepComments,
				ProcedureUpdateSuggestions: stepSuggestions,
				IsSkipped: status?.IsSkipped,
				IsOverridden: status?.IsOverridden,
				ListenerTags: enrichedListenerTags,
				ManualMeasurementValue: status?.ManualMeasurementValue,
				ManualMeasurementTimeStamp: status?.ManualMeasurementTimeStamp,
			};

			return enrichedStep;
		}) ?? [];

	// Create enriched checklist with all enriched steps
	const enrichedChecklist = checklist
		? {
				...checklist,
				Steps: enrichedSteps,
			}
		: undefined;

	return { data: enrichedChecklist, isLoading: false };
};

export default useGetChecklistWithLiveData;
